import { useCallback } from 'react';
import { useConfirmationDialog } from '../../../shared/ui/dialogs/useConfirmationDialog';

interface UseRepairSetupConfirmationArgs {
  resolvedProfileKey: string;
  repairableParameterNames: string[];
  onRepairConfirmed: (profileKey: string) => void;
}

/**
 * "Add missing setup parameters?" destructive-confirmation flow. The confirmed
 * marker itself stays in the parent (the auto-repair gate reads it before the
 * readiness hook exists, so it cannot be owned here); this hook owns the
 * confirm dialog and the message construction.
 */
export const useRepairSetupConfirmation = ({
  resolvedProfileKey,
  repairableParameterNames,
  onRepairConfirmed,
}: UseRepairSetupConfirmationArgs) => {
  const {
    confirmDestructive,
    cancelPendingConfirmation: cancelRepairSetupConfirmation,
    confirmationDialog: repairConfirmationDialog,
  } = useConfirmationDialog();

  const handleRepairSetupConfirm = useCallback(async () => {
    const repairableNames = repairableParameterNames;
    const parameterList =
      repairableNames.length > 0
        ? repairableNames.slice(0, 8).join(', ') +
          (repairableNames.length > 8 ? `, +${repairableNames.length - 8} more` : '')
        : '';
    const message = repairableNames.length > 0
      ? `The selected mapping profile requires ${repairableNames.length} PipeEX setup parameter${repairableNames.length === 1 ? '' : 's'} that the active Revit project does not contain: ${parameterList}. Continuing adds these parameters to Revit and replaces the current Mapping, Quick Fill, PCF Validation, and PCF export settings. Cancel to keep the project and workspace unchanged.`
      : 'The selected mapping profile requires PipeEX setup parameters that are missing from the active Revit project. Continuing adds them to Revit and replaces the current Mapping, Quick Fill, PCF Validation, and PCF export settings. Cancel to keep the project and workspace unchanged.';

    const confirmed = await confirmDestructive(
      'Add profile parameters and replace current settings?',
      message,
      'Add parameters and apply profile',
      'Keep current setup'
    );

    if (confirmed) {
      onRepairConfirmed(resolvedProfileKey);
    }
  }, [confirmDestructive, onRepairConfirmed, repairableParameterNames, resolvedProfileKey]);

  return {
    handleRepairSetupConfirm,
    cancelRepairSetupConfirmation,
    repairConfirmationDialog,
  };
};
